<template>
  <div v-if="isAdmin">
    <LocalizationProvider :language="kendoLang">
      <IntlProvider :locale="kendoLang">
        <Grid
          :loader="loader"
          id="grid"
          ref="grid"
          :data-items="gridData"
          :resizable="true"
          :page-size="DEFAULT_TAKE"
          @sortchange="sortChangeHandler"
          :total="total"
          :take="take"
          :skip="skip"
          :sort="sort"
          :sortable="true"
          @pagechange="pageChangeHandler"
          :columns="columns"
          :pageable="pageable"
        >
          <GridToolbar>
            <div class="grid-toolbar">
              <div class="flex-fill d-flex align-items-center">
                <div>
                  <b-form-input
                    v-model="searchWord"
                    type="text"
                    :debounce="500"
                    :placeholder="$t('lbl_EMPBE_SearchByKeyword')"
                  />
                </div>

                <div>
                  <b-button
                    variant="primary"
                    @click="onClickRefresh"
                    class="m-l-20"
                  >
                    <i class="fa fa-refresh" style="line-height: 1.5" />
                    {{ this.$t("lbl_EMPBE_Refresh") }}
                  </b-button>
                </div>
              </div>

              <div>
                <b-button variant="primary" @click="onClickAdd">
                  <i class="fa fa-plus-circle" style="line-height: 1.5" />
                  {{ this.$t("lbl_EMPBE_Add") }}
                </b-button>

                <b-button
                  class="m-l-20"
                  variant="secondary"
                  @click="onClickBatchAction"
                >
                  <i class="fa fa-edit" style="line-height: 1.5" />
                  {{ this.$t("lbl_EMPBE_BatchAction") }}
                </b-button>

                <b-button class="m-l-20" variant="success" @click="onExport">
                  <i
                    class="icofont icofont-upload-alt"
                    style="line-height: 1.5"
                  />
                  {{ this.$t("lbl_EMPBE_Export") }}
                </b-button>
              </div>
            </div>
          </GridToolbar>
          <!--Cell template Starts-->

          <template v-slot:commandsTemplate="{ props }">
            <td>
              <b-button
                variant="info"
                size="sm"
                @click="onClickEditRow(props.dataItem)"
              >
                {{ $t("lbl_EMPBE_Edit") }}
              </b-button>

              <b-button
                class="m-l-10"
                variant="danger"
                size="sm"
                @click="onClickDeleteRow(props.dataItem)"
              >
                {{ $t("lbl_EMPBE_Delete") }}
              </b-button>
            </td>
          </template>

          <!--Cell template Ends-->
        </Grid>
      </IntlProvider>
    </LocalizationProvider>

    <div>
      <div
        class="b-sidebar-backdrop bg-dark"
        style="z-index: 100"
        v-if="isShowForm"
        @click="onHideForm"
      ></div>
      <b-sidebar
        id="sideBarPopup"
        right
        bg-variant="white"
        :shadow="true"
        v-model="isShowForm"
        ref="sideBarPopup"
        no-header
        :no-close-on-esc="true"
        :no-close-on-route-change="true"
        body-class="overflow-hidden"
      >
        <OpenSearchWordSplitForm
          ref="openSearchWordSplitForm"
          :key="initFormData.key + initFormData.command + loader"
          :initForm="initFormData"
          @onHideForm="onHideForm"
          @onClickSave="onClickSave"
          @onClickUpdate="onClickUpdate"
          @onClickUpload="onClickUpload"
        />
      </b-sidebar>
    </div>
  </div>
</template>

<script>
import { IntlProvider, LocalizationProvider } from "@progress/kendo-vue-intl";
import { Grid, GridToolbar } from "@progress/kendo-vue-grid";
import { process } from "@progress/kendo-data-query";
import { openSearchService as service } from "@/_services";
import OpenSearchWordSplitForm from "./form.vue";
import { exportDataToExcel } from "@/_helpers/excel";

export default {
  components: {
    Grid,
    GridToolbar,
    LocalizationProvider,
    IntlProvider,
    OpenSearchWordSplitForm,
  },

  data() {
    return {
      initFormData: {},
      isShowForm: false,
      rawData: [
        // Data format
        // {
        //   cmd: "add",
        //   created: "2024-05-28 14:15:48",
        //   key: "colour",
        //   splitEnabled: false,
        //   status: "ACTIVE",
        //   updated: "2024-05-28 14:15:59",
        //   value: "colour",
        // },
      ],
      loader: false,
      group: [],
      sort: [
        // Default sorting format looks like
        // { field: "orderId", dir: "asc" },
      ],
      total: 0,
      take: 10, // Default page size
      skip: 0, // Default skipped pagenumber
      pageSizeValue: 10,
      columns: [
        {
          field: "key",
          title: this.$t("lbl_EMPBE_OpenSearch_Key"),
        },
        {
          field: "value",
          title: this.$t("lbl_EMPBE_OpenSearch_Value"),
        },
        {
          field: "created",
          title: this.$t("lbl_EMPBE_CreatedTime"),
        },
        {
          field: "updated",
          title: this.$t("lbl_EMPBE_UpdatedTime"),
        },
        {
          title: this.$t("lbl_EMPBE_Action"),
          cell: "commandsTemplate",
          width: "220px",
          sortable: false,
        },
      ],
      searchWord: "",
    };
  },

  created() {
    this.init();
  },

  computed: {
    user() {
      return this.$store.state.authentication.user;
    },

    isAdmin() {
      return this.user.userType?.toUpperCase() === "ADMIN";
    },

    langId() {
      return localStorage.getItem("currentLangId");
    },

    kendoLang() {
      switch (this.langId) {
        case "1252":
          return "en-US";
        case "950":
          return "zh-Hant";
        case "936":
          return "zh-Hans";
        default:
          return "zh-Hans";
      }
    },

    DEFAULT_TAKE: function () {
      return 10;
    },

    pageable() {
      return {
        buttonCount: 5,
        info: true,
        type: "numeric", // 'numeric', 'input'
        pageSizes: [10, 15, 50, "all"],
        previousNext: true,
        pageSizeValue: this.pageSizeValue,
      };
    },

    gridData: {
      get: function () {
        // Remove deleted data
        let finalData = this.filterPendingDeleteData(this.rawData);

        const searchWord = this.searchWord;

        if (searchWord) {
          finalData = finalData.filter(
            (x) =>
              x.key.includes(searchWord) ||
              x.value.includes(searchWord) ||
              x.created.includes(searchWord) ||
              x.updated.includes(searchWord)
          );
        }

        // Map Raw data into table
        this.total = finalData.length;

        return process(finalData, {
          take: this.take,
          skip: searchWord ? 0 : this.skip,
          sort: this.sort,
        });
      },
    },
  },

  watch: {
    // If isShowForm is changed, make body of page scrollable or not
    isShowForm: {
      handler() {
        if (this.isShowForm) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflowY = "scroll";
        }
      },
    },
  },

  methods: {
    init() {
      this.searchWord = "";

      this.initForm();
      // Get Data From server
      this.getData();
    },

    getData() {
      if (this.loader) {
        return;
      }

      var that = this;

      this.loader = true;

      const params = that.getParams();

      service
        .GetAllWordSplitData(params)
        .then((res) => {
          if (res.IsSuccessful) {
            that.rawData =
              res.data?.result?.map((x) => ({
                ...x,
                created: that.unixTimeStampToString(x.created),
                updated: that.unixTimeStampToString(x.updated),
              })) ?? [];
          }
        })
        .finally(() => {
          that.loader = false;
        });
    },

    getParams() {
      const skippedPage = this.skip ? this.skip / this.DEFAULT_TAKE : 0;
      const pageNum = skippedPage + 1;

      // Maximum number of records that can be inserted into Aliyun OpenSearch
      const MAX_AMOUNT_OF_RECRODS = 1000;

      return {
        word: "",
        pageNum: pageNum.toString(),
        pageSize: MAX_AMOUNT_OF_RECRODS,
      };
    },

    initForm() {
      this.initFormData = {
        command: "add", // "add" || "edit" || "batch"
        key: "",
        value: "",
      };
    },

    pageChangeHandler: function (event) {
      this.loader = true;

      this.skip = event.page.skip;
      this.take = event.event.value === "all" ? 1000 : event.page.take;

      this.pageSizeValue = event.event.value;

      window.scrollTo({ top: 0, behavior: "smooth" });
      this.loader = false;
    },

    sortChangeHandler: function (event) {
      this.loader = true;

      this.sort = event.sort;

      this.loader = false;
    },

    onClickEditRow: function (word) {
      this.initFormData = {
        command: "edit", // "add" || "edit" || "batch"
        key: word.key,
        value: word.value,
        created: word.created,
        updated: word.updated,
      };

      this.isShowForm = true;
    },

    onClickRefresh() {
      this.initForm();

      this.getData();
    },

    onClickAdd() {
      this.isShowForm = true;
    },

    onClickBatchAction() {
      this.initFormData.command = "batch";
      this.isShowForm = true;
    },

    /**
     * Displays a confirmation dialog and deletes the row if the user confirms.
     *
     * @param {{key: string; value: string}} form - The form object containing the key and value.
     * @return {void}
     */
    onClickDeleteRow: function (form) {
      this.$swal({
        text: this.$t("lbl_EMPBE_SureToDelete"),
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then((res) => {
        if (!res.isConfirmed) return;

        const params = [
          {
            cmd: "delete",
            key: form.key,
            value: form.value,
          },
        ];

        this.formAction(params, "delete");
      });
    },

    /**
     * Displays a confirmation dialog and updates the form if the user confirms.
     *
     * @param {{key: string; value: string; cmd: "add" | "delete"}} form - The form object containing the key and value.
     * @return {void}
     */
    onClickSave: function (form) {
      //Show Alert after validation
      this.$swal({
        text: this.$t("lbl_EMPBE_ConfirmToSave"),
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then(async (res) => {
        if (!res.isConfirmed) return;

        const params = [form];

        this.formAction(params, "add");
      });
    },

    /**
     * Displays a confirmation dialog and updates the form if the user confirms.
     *
     * @param {{key: string; value: string; cmd: "add" | "delete"}}} form - The form object containing the key and value.
     * @return {void}
     */
    onClickUpdate(form) {
      this.$swal({
        text: this.$t("lbl_EMPBE_ConfirmToSave"),
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then(async (res) => {
        if (!res.isConfirmed) return;

        const params = [form];

        this.formAction(params, "add");
      });
    },

    /**
     * Executes a single form action based on the provided form and action.
     *
     * @param {{key: string; value: string; cmd: "add" | "delete"}[]} params - The form object containing the key and value.
     * @param {"add" | "delete" | "batch"} action - The action to perform on the form. Can be "add", "delete".
     * @return {void}
     */
    formAction(params, action) {
      const that = this;

      that.setFormLoading(true);

      service
        .EditWordSplitData(params)
        .then(async (res) => {
          if (res.data?.httpCode || res.data?.message) {
            const message = `Request failed with HTTP code ${res.data?.httpCode}: ${res.data?.message}`;
            alert(message);

            const updateFailMessage = that.$t("lbl_EMPBE_UpdateFailed");

            await that.alertMsg(updateFailMessage).then(() => {
              that.isShowForm = false;

              that.init();
            });

            return;
          }

          const msgKey =
            action === "delete"
              ? "lbl_EMPBE_DeleteSuccess"
              : "lbl_EMPBE_SaveSuccess";
          const message = that.$t(msgKey);

          await that.alertMsg(message).then(() => {
            if (action === "batch") {
              window.location.reload();
              return;
            }

            if (action !== "delete") {
              that.isShowForm = false;
            }

            that.init();
          });
        })
        .finally(() => {
          that.setFormLoading(false);
        });
    },

    onClickUpload(forms) {
      this.$swal({
        text: this.$t("lbl_EMPBE_ConfirmToUpload"),
        showCancelButton: true,
        confirmButtonText: this.$t("lbl_EMPBE_Confirm"),
        cancelButtonText: this.$t("lbl_EMPBE_Cancel"),
      }).then(async (res) => {
        if (!res.isConfirmed) return;

        this.formAction(forms, "batch");
      });
    },

    onHideForm() {
      this.isShowForm = false;
      this.initForm();
    },

    alertMsg(message) {
      return this.$swal({
        text: message,
        showCancelButton: false,
      });
    },

    unixTimeStampToString: function (time) {
      try {
        const date = new Date(time * 1000);
        const MM = date.getMonth() + 1; // getMonth() is zero-based
        const dd = date.getDate();
        const hh = date.getHours();
        const mm = date.getMinutes();
        const ss = date.getSeconds();

        return [
          date.getFullYear() + "-",
          (MM > 9 ? "" : "0") + MM + "-",
          (dd > 9 ? "" : "0") + dd + " ",
          (hh > 9 ? "" : "0") + hh + ":",
          (mm > 9 ? "" : "0") + mm + ":",
          (ss > 9 ? "" : "0") + ss,
        ].join("");
      } catch (ex) {
        console.log(ex);
        return "";
      }
    },

    filterPendingDeleteData(rawData) {
      if (!rawData || !rawData.length) {
        return [];
      }

      return rawData.filter((x) => x.cmd !== "delete");
    },

    onExport() {
      const data = this.filterPendingDeleteData(this.rawData).map((x) => ({
        query: x.key,
        result: x.value,
        created: x.created,
        updated: x.updated,
      }));

      const params = {
        data,
        fileName: "OpenSearch_WordSplit",
        fileNameWithTimeStamp: true,
        writeFileOptions: { compression: true },
      };

      exportDataToExcel(params);
    },

    setFormLoading(loading) {
      this.$refs["openSearchWordSplitForm"].isLoading = loading;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../style/grid";
</style>
